import { apiRoutes } from '@/app/apiConfig/apiRoutes';
import { ResearchOption } from '@/app/shared-components/Types/Research';
import { apiSlice } from './apiSlice';
import { Research, ResearchStatusItem } from '../shared-components/Types/Order';
import { ordersApiSlice } from './ordersApiSlice';
import { arraysEqualByKeys } from '@/utils/helpers';

export const researchApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getResearchOptions: builder.query<ResearchOption[], void>({
      query: () => {
        return {
          url: `${apiRoutes.research}/options`,
          method: 'GET',
        };
      },
      providesTags: ['ResearchOptions'],
    }),
    researchOptionOrder: builder.mutation<ResearchStatusItem, { orderId: number; option: string }>({
      query: ({ orderId, option }) => {
        return {
          url: `${apiRoutes.research}/${orderId}/${option}`,
          method: 'POST',
        };
      },
      async onQueryStarted({ orderId, option }, { dispatch, queryFulfilled, getState }) {
        try {
          const optionName = researchApiSlice.endpoints.getResearchOptions
            .select()(getState())
            .data?.find((r) => r.slug === option)?.name;
          if (!optionName) {
            return;
          }
          console.log('optionName', optionName);
          const previousResearch = researchApiSlice.endpoints.getResearchInfo
            .select(orderId)(getState())
            .data?.researched?.find((r) => r.name === optionName);

          const { data: updatedResearchItem } = await queryFulfilled;

          // Update the cached research info with the new research item data
          dispatch(
            researchApiSlice.util.updateQueryData('getResearchInfo', orderId, (draft) => {
              if (draft.researched) {
                const index = draft.researched.findIndex((r) => r.name === optionName);
                if (index !== -1) {
                  draft.researched[index] = updatedResearchItem;
                } else {
                  draft.researched.push(updatedResearchItem);
                }
              }
            }),
          );

          if (
            previousResearch?.completedAt &&
            updatedResearchItem.completedAt &&
            previousResearch.completedAt !== updatedResearchItem.completedAt &&
            updatedResearchItem.isSuccess
          ) {
            dispatch(
              ordersApiSlice.endpoints.getOrderAllQuotes.initiate(orderId, {
                forceRefetch: true,
              }),
            );
          }
        } catch (e) {
          console.log('unable to update info', e);
        }
      },
      invalidatesTags: (_result, error, { orderId }) =>
        error ? [] : ['Orders', { type: 'Order' as const, id: orderId }, 'Quotes'],
    }),
    getResearchInfo: builder.query<Research, number>({
      query: (orderId) => {
        return {
          url: `${apiRoutes.research}/${orderId}`,
          method: 'GET',
        };
      },
      async onQueryStarted(orderId, { dispatch, queryFulfilled, getState }) {
        try {
          const previousResearch =
            researchApiSlice.endpoints.getResearchInfo.select(orderId)(getState()).data;

          const { data } = await queryFulfilled;

          if (
            previousResearch?.researched &&
            data.researched &&
            !arraysEqualByKeys(previousResearch.researched, data.researched, [
              'name',
              'completedAt',
            ])
          ) {
            dispatch(
              ordersApiSlice.endpoints.getOrderAllQuotes.initiate(orderId, {
                forceRefetch: true,
              }),
            );
          }
        } catch (e) {
          console.log('unable to update info', e);
        }
      },
    }),
  }),
});

export const {
  useGetResearchOptionsQuery,
  useResearchOptionOrderMutation,
  useGetResearchInfoQuery,
  useLazyGetResearchInfoQuery,
} = researchApiSlice;
